import { makeFunctionReference } from "convex/server";

export const convexApi = {
  bootstrap: {
    getAppData: makeFunctionReference<"query">("bootstrap:getAppData"),
    getAccessStatus: makeFunctionReference<"query">("bootstrap:getAccessStatus"),
    provisionObserverAccount: makeFunctionReference<"mutation">("bootstrap:provisionObserverAccount"),
  },
  discussions: {
    create: makeFunctionReference<"mutation">("discussions:create"),
    addContribution: makeFunctionReference<"mutation">("discussions:addContribution"),
    closeDiscussion: makeFunctionReference<"mutation">("discussions:closeDiscussion"),
  },
  governance: {
    createProposal: makeFunctionReference<"mutation">("governance:createProposal"),
    openProposalWorkflowFromDiscussion: makeFunctionReference<"mutation">("governance:openProposalWorkflowFromDiscussion"),
    scheduleVote: makeFunctionReference<"mutation">("governance:scheduleVote"),
    setProposalPatronage: makeFunctionReference<"mutation">("governance:setProposalPatronage"),
    submitAmendment: makeFunctionReference<"mutation">("governance:submitAmendment"),
  },
  voting: {
    castFirmVote: makeFunctionReference<"mutation">("voting:castFirmVote"),
    createDelegation: makeFunctionReference<"mutation">("voting:createDelegation"),
    revokeDelegation: makeFunctionReference<"mutation">("voting:revokeDelegation"),
  },
  legislation: {
    joinDraftingGroup: makeFunctionReference<"mutation">("legislation:joinDraftingGroup"),
    createLobbyingProject: makeFunctionReference<"mutation">("legislation:createLobbyingProject"),
    requestMandate: makeFunctionReference<"mutation">("legislation:requestMandate"),
    reviewMandate: makeFunctionReference<"mutation">("legislation:reviewMandate"),
    logExternalCommunication: makeFunctionReference<"mutation">("legislation:logExternalCommunication"),
    joinPosition: makeFunctionReference<"mutation">("legislation:joinPosition"),
    leavePosition: makeFunctionReference<"mutation">("legislation:leavePosition"),
    endorsePosition: makeFunctionReference<"mutation">("legislation:endorsePosition"),
    withdrawEndorsement: makeFunctionReference<"mutation">("legislation:withdrawEndorsement"),
    setPositionCall: makeFunctionReference<"mutation">("legislation:setPositionCall"),
    postPositionMessage: makeFunctionReference<"mutation">("legislation:postPositionMessage"),
    addPositionDocument: makeFunctionReference<"mutation">("legislation:addPositionDocument"),
  },
  admission: {
    proposeAdmission: makeFunctionReference<"mutation">("admission:proposeAdmission"),
    finalizeAdmission: makeFunctionReference<"mutation">("admission:finalizeAdmission"),
    proposeFirmAdmission: makeFunctionReference<"mutation">("admission:proposeFirmAdmission"),
    finalizeFirmAdmission: makeFunctionReference<"mutation">("admission:finalizeFirmAdmission"),
  },
  attachments: {
    generateUploadUrl: makeFunctionReference<"mutation">("attachments:generateUploadUrl"),
    addDiscussionAttachment: makeFunctionReference<"mutation">("attachments:addDiscussionAttachment"),
    removeAttachment: makeFunctionReference<"mutation">("attachments:removeAttachment"),
  },
  legislativeNews: {
    suggestSource: makeFunctionReference<"action">("legislativeNews:suggestSource"),
    refreshJurisdictionNow: makeFunctionReference<"action">("legislativeNews:refreshJurisdictionNow"),
    setPreferences: makeFunctionReference<"mutation">("legislativeNews:setPreferences"),
    reviewSnapshot: makeFunctionReference<"mutation">("legislativeNews:reviewSnapshot"),
    setSourceActive: makeFunctionReference<"mutation">("legislativeNews:setSourceActive"),
    updateSource: makeFunctionReference<"mutation">("legislativeNews:updateSource"),
    deleteSource: makeFunctionReference<"mutation">("legislativeNews:deleteSource"),
  },
  cooperation: {
    createRequest: makeFunctionReference<"mutation">("cooperation:createRequest"),
    addMessage: makeFunctionReference<"mutation">("cooperation:addMessage"),
    updateStatus: makeFunctionReference<"mutation">("cooperation:updateStatus"),
    updateConflictCheck: makeFunctionReference<"mutation">("cooperation:updateConflictCheck"),
    recordOutcome: makeFunctionReference<"mutation">("cooperation:recordOutcome"),
  },
  notifications: {
    markRead: makeFunctionReference<"mutation">("notifications:markRead"),
    markAllRead: makeFunctionReference<"mutation">("notifications:markAllRead"),
  },
  savedItems: {
    save: makeFunctionReference<"mutation">("savedItems:save"),
    remove: makeFunctionReference<"mutation">("savedItems:remove"),
  },
  recommendations: {
    hide: makeFunctionReference<"mutation">("recommendations:hide"),
    unhide: makeFunctionReference<"mutation">("recommendations:unhide"),
  },
  quickAccess: {
    pinPage: makeFunctionReference<"mutation">("quickAccess:pinPage"),
    removePin: makeFunctionReference<"mutation">("quickAccess:removePin"),
  },
  members: {
    generateProfilePhotoUploadUrl: makeFunctionReference<"mutation">("members:generateProfilePhotoUploadUrl"),
    updateMyProfile: makeFunctionReference<"mutation">("members:updateMyProfile"),
    updateMyFirmProfile: makeFunctionReference<"mutation">("members:updateMyFirmProfile"),
    createMyFirmProfile: makeFunctionReference<"mutation">("members:createMyFirmProfile"),
  },
  admin: {
    overview: makeFunctionReference<"query">("admin:overview"),
    listClerkAccounts: makeFunctionReference<"action">("admin:listClerkAccounts"),
    provisionAccount: makeFunctionReference<"mutation">("admin:provisionAccount"),
    removeDiscussion: makeFunctionReference<"mutation">("admin:removeDiscussion"),
    removeContribution: makeFunctionReference<"mutation">("admin:removeContribution"),
    userActivity: makeFunctionReference<"query">("admin:userActivity"),
    publicSiteOverview: makeFunctionReference<"query">("admin:publicSiteOverview"),
    generateReport: makeFunctionReference<"mutation">("admin:generateReport"),
    saveGovernanceSettings: makeFunctionReference<"mutation">("admin:saveGovernanceSettings"),
    upsertPublicContentDraft: makeFunctionReference<"mutation">("admin:upsertPublicContentDraft"),
    submitPublicContentChange: makeFunctionReference<"mutation">("admin:submitPublicContentChange"),
    reviewPublicContentChange: makeFunctionReference<"mutation">("admin:reviewPublicContentChange"),
    addPublicMediaAsset: makeFunctionReference<"mutation">("admin:addPublicMediaAsset"),
    retryPublicContentPublish: makeFunctionReference<"mutation">("admin:retryPublicContentPublish"),
    upsertFirm: makeFunctionReference<"mutation">("admin:upsertFirm"),
    updateFirm: makeFunctionReference<"mutation">("admin:updateFirm"),
    suspendFirm: makeFunctionReference<"mutation">("admin:suspendFirm"),
    removeFirm: makeFunctionReference<"mutation">("admin:removeFirm"),
    upsertPracticeArea: makeFunctionReference<"mutation">("admin:upsertPracticeArea"),
    upsertUser: makeFunctionReference<"mutation">("admin:upsertUser"),
    backfillUserMemberAccess: makeFunctionReference<"mutation">("admin:backfillUserMemberAccess"),
    suspendUser: makeFunctionReference<"mutation">("admin:suspendUser"),
    removeUser: makeFunctionReference<"mutation">("admin:removeUser"),
    resetUserMfa: makeFunctionReference<"action">("admin:resetUserMfa"),
    sendUserInvitation: makeFunctionReference<"mutation">("admin:sendUserInvitation"),
    sendUserInvitationEmail: makeFunctionReference<"action">("admin:sendUserInvitationEmail"),
    setFirmGovernanceAndPractices: makeFunctionReference<"mutation">("admin:setFirmGovernanceAndPractices"),
    createPracticeArea: makeFunctionReference<"mutation">("admin:createPracticeArea"),
    setPracticeAreaActive: makeFunctionReference<"mutation">("admin:setPracticeAreaActive"),
    upsertMembershipCategoryConfig: makeFunctionReference<"mutation">("admin:upsertMembershipCategoryConfig"),
    upsertAffiliationGroup: makeFunctionReference<"mutation">("admin:upsertAffiliationGroup"),
    upsertTopicTag: makeFunctionReference<"mutation">("admin:upsertTopicTag"),
    setTopicTagStatus: makeFunctionReference<"mutation">("admin:setTopicTagStatus"),
    mergeTopicTag: makeFunctionReference<"mutation">("admin:mergeTopicTag"),
    createJurisdictionTag: makeFunctionReference<"mutation">("admin:createJurisdictionTag"),
    setJurisdictionTagActive: makeFunctionReference<"mutation">("admin:setJurisdictionTagActive"),
    setDefaultConfidentiality: makeFunctionReference<"mutation">("admin:setDefaultConfidentiality"),
    saveAdminNote: makeFunctionReference<"mutation">("admin:saveAdminNote"),
  },
  publicMembership: {
    listForAdmin: makeFunctionReference<"query">("publicMembership:listForAdmin"),
    review: makeFunctionReference<"mutation">("publicMembership:review"),
    reviewAndEmail: makeFunctionReference<"action">("publicMembership:reviewAndEmail"),
  },
};
